<template>
  <div class="space-y-4">
    <!-- Top Summary Realization Metric Bar -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
      <!-- 1. Total Students -->
      <div class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-2xs">
        <div class="flex items-center justify-between text-slate-500 dark:text-[#7E8DA5] mb-1">
          <span class="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">Audited Students</span>
          <Users class="w-3.5 h-3.5 text-indigo-500" />
        </div>
        <div class="text-base sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] font-mono">
          {{ filteredStudents.length }}
        </div>
        <span class="text-[10px] text-slate-400 dark:text-[#7E8DA5] block mt-0.5">Across selected classes</span>
      </div>

      <!-- 2. Total Expected -->
      <div class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-2xs">
        <div class="flex items-center justify-between text-indigo-500 mb-1">
          <span class="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">Total Expected</span>
          <TrendingUp class="w-3.5 h-3.5" />
        </div>
        <div class="text-base sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] font-mono">
          ₹{{ (ledgerSummary.totalExpected || 0).toLocaleString("en-IN") }}
        </div>
        <span class="text-[10px] text-slate-400 dark:text-[#7E8DA5] block mt-0.5">All sessions + items</span>
      </div>

      <!-- 3. Total Paid ("Got This") -->
      <div class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-2xs">
        <div class="flex items-center justify-between text-emerald-500 mb-1">
          <span class="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">Total Paid ("Got This")</span>
          <CheckCircle2 class="w-3.5 h-3.5" />
        </div>
        <div class="text-base sm:text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
          ₹{{ (ledgerSummary.totalPaid || 0).toLocaleString("en-IN") }}
        </div>
        <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold block mt-0.5">
          {{ ledgerSummary.collectionRate }}% Lifetime Realization
        </span>
      </div>

      <!-- 4. Total Pending ("Left Not Paid") -->
      <div class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-2xs">
        <div class="flex items-center justify-between text-rose-500 mb-1">
          <span class="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">Pending / Due ("Left")</span>
          <AlertCircle class="w-3.5 h-3.5" />
        </div>
        <div class="text-base sm:text-xl font-black text-rose-600 dark:text-rose-400 font-mono">
          ₹{{ (ledgerSummary.totalOutstanding || 0).toLocaleString("en-IN") }}
        </div>
        <span class="text-[10px] text-rose-500 dark:text-rose-400 font-semibold block mt-0.5">
          {{ ledgerSummary.studentsWithDues }} students with dues
        </span>
      </div>
    </div>

    <!-- Filter & Search Toolbar -->
    <div class="bg-white dark:bg-[#111C30] p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <!-- Search Input -->
        <div class="relative flex-1">
          <Search class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search student by name, roll number, or phone..."
            class="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
        </div>

        <!-- Filter Controls -->
        <div class="flex items-center gap-2 flex-wrap">
          <!-- Class Filter -->
          <select
            v-model="selectedClass"
            class="px-3 py-2 text-xs font-semibold bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] focus:outline-none"
          >
            <option value="">All Current Classes</option>
            <option v-for="cls in availableClasses" :key="cls._id" :value="cls.name">
              {{ cls.name }} {{ cls.section ? `(${cls.section})` : '' }}
            </option>
          </select>

          <!-- Dues Status Filter -->
          <select
            v-model="statusFilter"
            class="px-3 py-2 text-xs font-semibold bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="has_dues">Has Pending Dues (Dues > 0)</option>
            <option value="has_arrears">Has Previous Class Arrears</option>
            <option value="cleared">Cleared (₹0 Left)</option>
            <option value="overdue">Overdue Only</option>
          </select>

          <!-- Specific Class Arrears Filter (Class 1, Class 2, etc.) -->
          <select
            v-model="classArrearFilter"
            class="px-3 py-2 text-xs font-semibold bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] focus:outline-none"
          >
            <option value="">Filter by Class Dues</option>
            <option v-for="cls in distinctClassNames" :key="'arrear-' + cls" :value="cls">
              Dues in {{ cls }}
            </option>
          </select>

          <!-- Reset Filter Button -->
          <button
            v-if="searchQuery || selectedClass || statusFilter !== 'all' || classArrearFilter"
            @click="resetFilters"
            class="px-2.5 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition"
            title="Reset Filters"
          >
            Reset
          </button>
        </div>
      </div>
    </div>

    <!-- Master Ledger Container -->
    <div class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden">
      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center flex flex-col items-center justify-center">
        <AppLoader
          variant="card"
          size="md"
          message="Loading Comprehensive Student Ledger..."
          subtext="Calculating multi-class balances and historical realization"
          color="indigo"
        />
      </div>

      <div v-else>
        <!-- Desktop Table View (hidden below lg) -->
        <div class="hidden lg:block overflow-x-auto">
          <table class="w-full text-left text-xs min-w-[1000px]">
            <thead class="bg-slate-50/90 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200/80 dark:border-[#263449]">
              <tr>
                <th class="py-3 px-4">#</th>
                <th class="py-3 px-4">Student</th>
                <th class="py-3 px-4">Current Class</th>
                <th class="py-3 px-4 text-right">Lifetime Expected</th>
                <th class="py-3 px-4 text-right">Got / Paid</th>
                <th class="py-3 px-4 text-right">Total Left / Due</th>
                <th class="py-3 px-4">Class-by-Class Remaining Breakdown</th>
                <th class="py-3 px-4 text-center">Status</th>
                <th class="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
              <tr
                v-for="(s, idx) in filteredStudents"
                :key="s._id"
                class="hover:bg-slate-50/70 dark:hover:bg-[#1A2840]/60 transition"
              >
                <!-- # -->
                <td class="py-3.5 px-4 font-mono text-slate-400">
                  {{ idx + 1 }}
                </td>

                <!-- Student Identity -->
                <td class="py-3.5 px-4">
                  <div class="flex items-center gap-2.5">
                    <div class="w-8 h-8 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                      <img
                        v-if="s.photo"
                        :src="s.photo"
                        :alt="s.name"
                        class="w-full h-full object-cover"
                      />
                      <div v-else class="w-full h-full flex items-center justify-center font-bold text-slate-500 text-xs">
                        {{ s.name?.charAt(0) || "S" }}
                      </div>
                    </div>
                    <div class="min-w-0">
                      <div class="font-bold text-slate-900 dark:text-[#F1F5F9] truncate">
                        {{ s.name }}
                      </div>
                      <div class="text-[10px] text-slate-400 font-mono">
                        Roll: {{ s.rollNumber || "N/A" }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Current Class & Session -->
                <td class="py-3.5 px-4">
                  <span class="font-bold text-slate-800 dark:text-[#F1F5F9] block">
                    {{ s.class?.name || "N/A" }} <span v-if="s.class?.section">({{ s.class.section }})</span>
                  </span>
                  <span class="text-[10px] text-slate-400 block font-mono">
                    Session: {{ s.session || "Current" }}
                  </span>
                </td>

                <!-- Expected -->
                <td class="py-3.5 px-4 text-right font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">
                  ₹{{ ((s.lifetimeExpected || (s.totalPaid + s.totalOutstanding)) || 0).toLocaleString("en-IN") }}
                </td>

                <!-- Total Paid -->
                <td class="py-3.5 px-4 text-right font-mono font-black text-emerald-600 dark:text-emerald-400">
                  ₹{{ (s.totalPaid || 0).toLocaleString("en-IN") }}
                </td>

                <!-- Total Outstanding / Left -->
                <td class="py-3.5 px-4 text-right font-mono font-black" :class="(s.totalOutstanding || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                  ₹{{ (s.totalOutstanding || 0).toLocaleString("en-IN") }}
                </td>

                <!-- Class Breakdown Badges (Class 1 Remaining, Class 2 Remaining) -->
                <td class="py-3.5 px-4">
                  <div class="flex flex-wrap gap-1.5 max-w-xs">
                    <template v-if="s.classBreakdown && s.classBreakdown.length > 0">
                      <span
                        v-for="cb in s.classBreakdown"
                        :key="cb.session + '-' + cb.className"
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold"
                        :class="
                          cb.remaining > 0
                            ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200/80 dark:border-rose-900/40'
                            : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-900/40'
                        "
                      >
                        <span class="w-1.5 h-1.5 rounded-full" :class="cb.remaining > 0 ? 'bg-rose-500' : 'bg-emerald-500'"></span>
                        <span>{{ cb.className }}: <strong>{{ cb.remaining > 0 ? `₹${cb.remaining.toLocaleString("en-IN")} Left` : 'Cleared ✓' }}</strong></span>
                      </span>
                    </template>
                    <span v-else class="text-slate-400 text-[11px] italic">
                      {{ (s.totalOutstanding || 0) === 0 ? "✓ Cleared (₹0 Left)" : `₹${(s.totalOutstanding || 0).toLocaleString("en-IN")} Left` }}
                    </span>
                  </div>
                </td>

                <!-- Overall Status Badge -->
                <td class="py-3.5 px-4 text-center">
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block font-mono"
                    :class="
                      (s.totalOutstanding || 0) === 0
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : s.monthlyFee?.overdueCount > 0
                        ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                    "
                  >
                    {{ (s.totalOutstanding || 0) === 0 ? "Cleared" : (s.monthlyFee?.overdueCount > 0 ? "Overdue" : "Pending") }}
                  </span>
                </td>

                <!-- Action Buttons -->
                <td class="py-3.5 px-4 text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <button
                      v-if="(s.totalOutstanding || 0) > 0"
                      @click="$emit('record-payment', s)"
                      class="px-2.5 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-lg border border-emerald-200/80 transition cursor-pointer"
                      title="Collect Fee"
                    >
                      Collect
                    </button>
                    <button
                      @click="$emit('post-adjustment', s)"
                      class="px-2 py-1 text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 rounded-lg border border-amber-200/80 transition cursor-pointer"
                      title="Add Debit / Credit (ID Card, Badge, etc.)"
                    >
                      +/-
                    </button>
                    <button
                      @click="$emit('view-details', s._id || s)"
                      class="px-2.5 py-1 text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300 rounded-lg border border-indigo-200/80 transition cursor-pointer"
                      title="Full Profile & Ledger"
                    >
                      Audit
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredStudents.length === 0">
                <td colspan="9" class="p-12 text-center text-slate-400 text-xs">
                  <BookOpenText class="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  No student records match your selected class, session, or arrears filter.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card List View (block lg:hidden) -->
        <div class="block lg:hidden p-3 sm:p-4 space-y-3 bg-slate-50/60 dark:bg-[#0B1120]/40">
          <div
            v-for="s in filteredStudents"
            :key="'m-s-' + s._id"
            class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] p-4 space-y-3 shadow-2xs"
          >
            <!-- Top Row: Student Name, Class & Status -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-2.5 min-w-0">
                <div class="w-9 h-9 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                  <img v-if="s.photo" :src="s.photo" :alt="s.name" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex items-center justify-center font-bold text-slate-500 text-xs">
                    {{ s.name?.charAt(0) || "S" }}
                  </div>
                </div>
                <div class="min-w-0">
                  <h4 class="font-bold text-sm text-slate-900 dark:text-[#F1F5F9] truncate">{{ s.name }}</h4>
                  <p class="text-[11px] text-slate-500 dark:text-[#7E8DA5]">
                    Roll: {{ s.rollNumber || "—" }} • {{ s.class?.name || "N/A" }}
                  </p>
                </div>
              </div>
              <span
                class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0"
                :class="
                  (s.totalOutstanding || 0) === 0
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                "
              >
                {{ (s.totalOutstanding || 0) === 0 ? "Cleared" : "Pending" }}
              </span>
            </div>

            <!-- Numbers Grid: Expected, Paid, Pending -->
            <div class="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-[#18243A] p-2.5 rounded-xl border border-slate-100 dark:border-[#263449] text-xs">
              <div>
                <span class="text-[9px] font-bold text-slate-400 uppercase block">Expected</span>
                <span class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">
                  ₹{{ ((s.lifetimeExpected || (s.totalPaid + s.totalOutstanding)) || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase block">Got This</span>
                <span class="font-mono font-black text-emerald-600 dark:text-emerald-400">
                  ₹{{ (s.totalPaid || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[9px] font-bold text-rose-600 dark:text-rose-400 uppercase block">Left / Due</span>
                <span class="font-mono font-black" :class="(s.totalOutstanding || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                  ₹{{ (s.totalOutstanding || 0).toLocaleString("en-IN") }}
                </span>
              </div>
            </div>

            <!-- Class-by-Class Remaining Breakdown Tags -->
            <div v-if="s.classBreakdown && s.classBreakdown.length > 0" class="space-y-1">
              <span class="text-[10px] font-bold text-slate-400 uppercase block">Class Remaining:</span>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="cb in s.classBreakdown"
                  :key="'m-cb-' + cb.session + '-' + cb.className"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold"
                  :class="
                    cb.remaining > 0
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200/80 dark:border-rose-900/40'
                      : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-900/40'
                  "
                >
                  <span>{{ cb.className }}: <strong>{{ cb.remaining > 0 ? `₹${cb.remaining.toLocaleString("en-IN")} Left` : 'Cleared ✓' }}</strong></span>
                </span>
              </div>
            </div>

            <!-- Action Buttons Bar -->
            <div class="flex items-center gap-2 pt-1">
              <button
                v-if="(s.totalOutstanding || 0) > 0"
                @click="$emit('record-payment', s)"
                class="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold text-center transition cursor-pointer"
              >
                Collect Fee
              </button>
              <button
                @click="$emit('post-adjustment', s)"
                class="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200/80 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                +/- Charge
              </button>
              <button
                @click="$emit('view-details', s._id || s)"
                class="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200/80 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Audit
              </button>
            </div>
          </div>

          <!-- Empty Mobile State -->
          <div v-if="filteredStudents.length === 0" class="py-12 text-center text-slate-400 text-xs">
            No matching student records found.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import {
    Users,
    TrendingUp,
    CheckCircle2,
    AlertCircle,
    Search,
    BookOpenText,
  } from "lucide-vue-next";

  const props = defineProps({
    students: {
      type: Array,
      default: () => [],
    },
    classesList: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["record-payment", "post-adjustment", "view-details"]);

  const searchQuery = ref("");
  const selectedClass = ref("");
  const statusFilter = ref("all");
  const classArrearFilter = ref("");

  const resetFilters = () => {
    searchQuery.value = "";
    selectedClass.value = "";
    statusFilter.value = "all";
    classArrearFilter.value = "";
  };

  const availableClasses = computed(() => props.classesList || []);

  // Distinct class names that appear across student breakdowns
  const distinctClassNames = computed(() => {
    const set = new Set();
    (props.students || []).forEach((s) => {
      if (s.class?.name) set.add(s.class.name);
      (s.classBreakdown || []).forEach((cb) => {
        if (cb.className && cb.className !== "Current Class" && cb.className !== "Previous Class") {
          set.add(cb.className);
        }
      });
    });
    return Array.from(set).sort();
  });

  // Filtered Students list
  const filteredStudents = computed(() => {
    let list = props.students || [];

    // Search
    if (searchQuery.value && searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase().trim();
      list = list.filter(
        (s) =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.rollNumber && s.rollNumber.toString().includes(q)) ||
          (s.phone && s.phone.includes(q))
      );
    }

    // Current Class
    if (selectedClass.value) {
      list = list.filter((s) => s.class?.name === selectedClass.value);
    }

    // Status filter
    if (statusFilter.value === "has_dues") {
      list = list.filter((s) => (s.totalOutstanding || 0) > 0);
    } else if (statusFilter.value === "has_arrears") {
      list = list.filter((s) => (s.previousClassesRemaining || 0) > 0);
    } else if (statusFilter.value === "cleared") {
      list = list.filter((s) => (s.totalOutstanding || 0) === 0);
    } else if (statusFilter.value === "overdue") {
      list = list.filter((s) => s.monthlyFee?.overdueCount > 0);
    }

    // Specific Class Arrear filter (e.g. Class 1 dues remaining)
    if (classArrearFilter.value) {
      list = list.filter((s) => {
        const match = (s.classBreakdown || []).find(
          (cb) => cb.className === classArrearFilter.value && cb.remaining > 0
        );
        return !!match;
      });
    }

    return list;
  });

  // Overall ledger aggregates for top metric cards
  const ledgerSummary = computed(() => {
    const list = filteredStudents.value;
    const totalExpected = list.reduce(
      (sum, s) => sum + (s.lifetimeExpected || ((s.totalPaid || 0) + (s.totalOutstanding || 0))),
      0
    );
    const totalPaid = list.reduce((sum, s) => sum + (s.totalPaid || 0), 0);
    const totalOutstanding = list.reduce((sum, s) => sum + (s.totalOutstanding || 0), 0);
    const studentsWithDues = list.filter((s) => (s.totalOutstanding || 0) > 0).length;
    const collectionRate =
      totalExpected > 0 ? Number(((totalPaid / totalExpected) * 100).toFixed(1)) : 100;

    return {
      totalExpected,
      totalPaid,
      totalOutstanding,
      studentsWithDues,
      collectionRate,
    };
  });
</script>
